import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@shared/components/ui';
import { AlertCircle, ArrowRight, CheckCircle, CreditCard } from 'lucide-react';
import { focusRing } from '@shared/utils/statusTone';
import { useEffect, useState } from 'react';
import { planApi } from '../api/planApi';

export default function BillingSuccess() {
  const { t } = useTranslation('billing');
  const [searchParams] = useSearchParams();
  const hasTrial = searchParams.get('trial') === 'true';
  const hasCheckoutSession = Boolean(searchParams.get('session_id'));
  const [verifiedTrial, setVerifiedTrial] = useState(false);
  useEffect(() => {
    let current = true;
    setVerifiedTrial(false);
    if (hasTrial) {
      planApi
        .getSubscription()
        .then((data) => {
          if (current) {
            setVerifiedTrial(data.subscription?.status?.toUpperCase() === 'TRIALING');
          }
        })
        .catch(() => {
          if (current) setVerifiedTrial(false);
        });
    }
    return () => {
      current = false;
    };
  }, [hasTrial]);
  const isConfirmed = hasTrial && verifiedTrial;

  const title = isConfirmed
    ? t('billingSuccess.trialTitle')
    : hasTrial
      ? t('billingSuccess.trialUnconfirmedTitle')
      : hasCheckoutSession
        ? t('billingSuccess.pendingTitle')
        : t('billingSuccess.unconfirmedTitle');
  const message = isConfirmed
    ? t('billingSuccess.trialMessage')
    : hasTrial
      ? t('billingSuccess.trialUnconfirmedMessage')
      : hasCheckoutSession
        ? t('billingSuccess.pendingMessage')
        : t('billingSuccess.unconfirmedMessage');
  const primaryLinkClass = `inline-flex w-full sm:w-auto min-h-11 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors bg-brand-600 text-on-accent-light shadow-lg shadow-brand-200 hover:bg-brand-700 dark:bg-brand-400 dark:text-on-accent-dark dark:shadow-none dark:hover:bg-brand-300 ${focusRing}`;
  const secondaryLinkClass = `inline-flex w-full sm:w-auto min-h-11 items-center justify-center gap-2 rounded-lg border-2 border-border-strong-light px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:border-brand-600 hover:text-brand-600 dark:border-border-strong-dark dark:text-slate-200 dark:hover:border-brand-400 dark:hover:text-brand-400 ${focusRing}`;

  return (
    <div className="mx-auto w-full max-w-2xl animate-in fade-in duration-300">
      <Card className="p-5 text-center sm:p-8">
        <div
          className={`mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full sm:mb-6 sm:h-20 sm:w-20 ${
            isConfirmed
              ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark'
              : 'bg-state-warn-bg-light dark:bg-state-warn-bg-dark'
          }`}
          aria-hidden="true"
        >
          {isConfirmed ? (
            <CheckCircle className="h-9 w-9 text-state-ok-light dark:text-state-ok-dark sm:h-10 sm:w-10" />
          ) : (
            <AlertCircle className="h-9 w-9 text-state-warn-light dark:text-state-warn-dark sm:h-10 sm:w-10" />
          )}
        </div>

        <h1
          id="billing-success-title"
          className="mb-3 text-2xl font-black text-slate-900 dark:text-white sm:mb-4 sm:text-3xl"
        >
          {title}
        </h1>
        <p
          role={isConfirmed ? 'status' : 'alert'}
          className="mx-auto mb-6 max-w-lg text-text-muted-light dark:text-text-muted-dark sm:mb-8"
        >
          {message}
        </p>

        {isConfirmed && (
          <section className="mb-6 rounded-xl bg-surface-2-light p-4 text-left dark:bg-surface-2-dark sm:mb-8 sm:p-6">
            <h2 className="mb-4 flex items-center gap-2 text-sm font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
              <CreditCard className="h-4 w-4" aria-hidden="true" />
              {t('billingSuccess.nextStepsTitle')}
            </h2>
            <ul className="space-y-3 text-sm text-text-muted-light dark:text-text-muted-dark">
              <li className="flex items-start gap-2">
                <CheckCircle
                  className="mt-0.5 h-4 w-4 shrink-0 text-state-ok-light dark:text-state-ok-dark"
                  aria-hidden="true"
                />
                <span>{t('billingSuccess.featuresAvailable')}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle
                  className="mt-0.5 h-4 w-4 shrink-0 text-state-ok-light dark:text-state-ok-dark"
                  aria-hidden="true"
                />
                <span>{t('billingSuccess.manageBilling')}</span>
              </li>
              {!hasTrial && (
                <li className="flex items-start gap-2">
                  <CheckCircle
                    className="mt-0.5 h-4 w-4 shrink-0 text-state-ok-light dark:text-state-ok-dark"
                    aria-hidden="true"
                  />
                  <span>{t('billingSuccess.receipt')}</span>
                </li>
              )}
            </ul>
          </section>
        )}

        <nav
          aria-label={t('billingSuccess.navigation')}
          className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4"
        >
          <Link to="/billing" className={primaryLinkClass}>
            {t('billingSuccess.goBilling')}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          <Link to="/dashboard" className={secondaryLinkClass}>
            {t('billingSuccess.goDashboard')}
          </Link>
        </nav>
      </Card>

      <p className="mt-5 text-center text-sm text-text-muted-light dark:text-text-muted-dark sm:mt-6">
        {t('billingSuccess.questions')}{' '}
        <a
          href="mailto:support@pgkb.com"
          className={`font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 ${focusRing}`}
        >
          {t('billingSuccess.contactSupport')}
        </a>
      </p>
    </div>
  );
}
